import { useState, useEffect, useRef } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { 
  Pin, 
  Crown, 
  Mic2, 
  Trophy, 
  X, 
  Sparkles, 
  CheckCircle,
  Clock,
  Flame
} from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



/**
 * Pinned Message Display
 * Shows messages pinned via Stage Flame gift
 */
export function PinnedMessageDisplay({ pinnedMessages = [], onExpire }) {
  const [activeMessage, setActiveMessage] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);

  // Find active message
  const activeMsg = pinnedMessages.find(m => {
    const now = new Date().toISOString();
    return !m.expires_at || m.expires_at > now;
  });

  useEffect(() => {
    setActiveMessage(activeMsg || null);
  }, [activeMsg?.effect_id]); // Only update when effect_id changes

  useEffect(() => {
    if (!activeMessage?.expires_at) return;

    const interval = setInterval(() => {
      const now = new Date();
      const expires = new Date(activeMessage.expires_at);
      const remaining = Math.max(0, Math.floor((expires - now) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        setActiveMessage(null);
        if (onExpire) onExpire(activeMessage.effect_id);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [activeMessage]);

  if (!activeMessage) return null;

  return (
    <div 
      className="bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/40 rounded-lg p-3 mb-3 animate-pulse"
      data-testid="pinned-message"
    >
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-orange-500/30 flex items-center justify-center">
            <Flame className="w-4 h-4 text-orange-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Pin className="w-3 h-3 text-orange-400" />
            <span className="text-xs font-medium text-orange-400">Pinned by {activeMessage.sender_name}</span>
            {timeLeft > 0 && (
              <Badge className="ml-auto text-[10px] bg-orange-500/20 text-orange-300">
                <Clock className="w-3 h-3 mr-1" />
                {timeLeft}s
              </Badge>
            )}
          </div>
          <p className="text-sm text-foreground break-words">
            {activeMessage.pinned_message || activeMessage.message || "🔥 Stage Flame!"}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Shoutout Request Notification
 * Shows pending shoutout requests to the host
 */
export function ShoutoutRequestPanel({ 
  pendingShoutouts = [], 
  isHost = false,
  streamId,
  getAuthHeaders,
  onComplete 
}) {
  const [completing, setCompleting] = useState(null);

  const handleCompleteShoutout = async (effectId) => {
    setCompleting(effectId);
    try {
      const response = await fetch(
        `${API}/live/streams/${streamId}/shoutout/${effectId}/complete`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        toast.success('Shoutout completed!');
        if (onComplete) onComplete(effectId);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to complete shoutout');
      }
    } catch (error) {
      console.error('Error completing shoutout:', error);
      toast.error('Failed to complete shoutout');
    } finally {
      setCompleting(null);
    }
  };

  // Only show to host
  if (!isHost || pendingShoutouts.length === 0) return null;

  return (
    <div className="space-y-2 mb-3" data-testid="shoutout-requests">
      {pendingShoutouts.map((shoutout) => (
        <div 
          key={shoutout.effect_id}
          className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-lg p-3 animate-bounce-slow"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center">
              <Mic2 className="w-5 h-5 text-purple-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-300">
                🎤 Mic Drop from {shoutout.sender_name}!
              </p>
              <p className="text-xs text-muted-foreground">
                Give them a shoutout!
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => handleCompleteShoutout(shoutout.effect_id)}
              disabled={completing === shoutout.effect_id}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {completing === shoutout.effect_id ? (
                'Done...'
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Done
                </>
              )}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * VIP User Indicator
 * Shows when a user has active VIP highlight
 */
export function VIPHighlightBadge({ isVIP = false, expiresAt = null }) {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!isVIP || !expiresAt) return;

    const interval = setInterval(() => {
      const now = new Date();
      const expires = new Date(expiresAt);
      const remaining = Math.max(0, Math.floor((expires - now) / 1000));
      setTimeLeft(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [isVIP, expiresAt]);

  if (!isVIP) return null;

  return (
    <Badge className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black text-[10px] px-1.5 py-0 animate-pulse">
      <Crown className="w-3 h-3 mr-0.5" />
      VIP
      {timeLeft > 0 && <span className="ml-1">{Math.floor(timeLeft / 60)}m</span>}
    </Badge>
  );
}

/**
 * Gift Effect Notification
 * Shows when a special gift is sent
 */
export function GiftEffectNotification({ effect, onDismiss }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Auto-dismiss after 5 seconds for non-persistent effects
    if (effect.effect_type !== 'pin_message') {
      const timer = setTimeout(() => {
        setVisible(false);
        if (onDismiss) onDismiss(effect.effect_id);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [effect]);

  if (!visible) return null;

  const effectConfig = {
    host_shoutout: {
      icon: Mic2,
      title: 'Mic Drop!',
      color: 'from-purple-500/30 to-pink-500/30',
      borderColor: 'border-purple-500/50'
    },
    vip_highlight: {
      icon: Crown,
      title: 'VIP Crown!',
      color: 'from-amber-500/30 to-yellow-500/30',
      borderColor: 'border-amber-500/50'
    },
    pin_message: {
      icon: Flame,
      title: 'Stage Flame!',
      color: 'from-orange-500/30 to-red-500/30',
      borderColor: 'border-orange-500/50'
    },
    profile_badge: {
      icon: Trophy,
      title: 'Soundstage Trophy!',
      color: 'from-yellow-500/30 to-amber-500/30',
      borderColor: 'border-yellow-500/50'
    }
  };

  const config = effectConfig[effect.effect_type] || effectConfig.host_shoutout;
  const Icon = config.icon;

  return (
    <div 
      className={cn(
        "bg-gradient-to-r p-3 rounded-lg border mb-2 animate-slide-in",
        config.color,
        config.borderColor
      )}
      data-testid={`effect-notification-${effect.effect_type}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold">{config.title}</p>
          <p className="text-xs text-muted-foreground">
            {effect.description || `${effect.sender_name} sent a special gift!`}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => {
            setVisible(false);
            if (onDismiss) onDismiss(effect.effect_id);
          }}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Active Effects Container
 * Main component that fetches and displays all active effects for a stream
 */
export default function LiveStreamEffects({
  streamId,
  isHost = false,
  currentUserId,
  getAuthHeaders
}) {
  const [effects, setEffects] = useState({
    pinned_messages: [],
    vip_users: [],
    pending_shoutouts: [],
    all_effects: []
  });
  const [recentEffects, setRecentEffects] = useState([]);
  const [userChatEffects, setUserChatEffects] = useState({ vip_highlight: false });
  const pollRef = useRef(null);
  const seenEffectsRef = useRef(new Set());

  // Define fetch functions before useEffect
  const fetchEffects = async () => {
    try {
      const response = await fetch(`${API}/live/streams/${streamId}/effects`);
      if (response.ok) {
        const data = await response.json();
        
        // Check for new effects to show notifications
        const newEffects = data.all_effects.filter(
          e => !seenEffectsRef.current.has(e.effect_id)
        );
        
        if (newEffects.length > 0) {
          newEffects.forEach(e => seenEffectsRef.current.add(e.effect_id));
          setRecentEffects(prev => [...prev, ...newEffects]);
        }
        
        setEffects(data);
      }
    } catch (error) {
      console.error('Error fetching effects:', error);
    }
  };

  const fetchUserChatEffects = async () => {
    if (!currentUserId) return;
    try {
      const response = await fetch(
        `${API}/live/streams/${streamId}/chat-effects/${currentUserId}`
      );
      if (response.ok) {
        const data = await response.json();
        setUserChatEffects(data);
      }
    } catch (error) {
      console.error('Error fetching user chat effects:', error);
    }
  };

  useEffect(() => {
    fetchEffects();
    // Poll every 3 seconds
    pollRef.current = setInterval(fetchEffects, 3000);
    
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  useEffect(() => {
    fetchUserChatEffects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, streamId]);

  const handleShoutoutComplete = (effectId) => {
    setEffects(prev => ({
      ...prev,
      pending_shoutouts: prev.pending_shoutouts.filter(s => s.effect_id !== effectId)
    }));
  };

  const handleDismissNotification = (effectId) => {
    setRecentEffects(prev => prev.filter(e => e.effect_id !== effectId));
  };

  const handlePinnedExpire = (effectId) => {
    setEffects(prev => ({
      ...prev,
      pinned_messages: prev.pinned_messages.filter(m => m.effect_id !== effectId)
    }));
  };

  return (
    <div className="space-y-2" data-testid="live-stream-effects">
      {/* Effect Notifications */}
      {recentEffects.slice(-3).map(effect => (
        <GiftEffectNotification
          key={effect.effect_id}
          effect={effect}
          onDismiss={handleDismissNotification}
        />
      ))}

      {/* Shoutout Requests (Host Only) */}
      <ShoutoutRequestPanel
        pendingShoutouts={effects.pending_shoutouts}
        isHost={isHost}
        streamId={streamId}
        getAuthHeaders={getAuthHeaders}
        onComplete={handleShoutoutComplete}
      />

      {/* Pinned Messages */}
      <PinnedMessageDisplay
        pinnedMessages={effects.pinned_messages}
        onExpire={handlePinnedExpire}
      />

      {/* User's own VIP status indicator */}
      {userChatEffects.vip_highlight && (
        <div className="text-center py-1">
          <VIPHighlightBadge 
            isVIP={true} 
            expiresAt={userChatEffects.vip_expires_at} 
          />
          <p className="text-xs text-amber-400 mt-1">Your messages are highlighted!</p>
        </div>
      )}
    </div>
  );
}

/**
 * Helper function to check if a user has VIP status in the effects list
 */
export function isUserVIP(userId, vipUsers = []) {
  const now = new Date().toISOString();
  return vipUsers.some(
    v => v.sender_user_id === userId && (!v.expires_at || v.expires_at > now)
  );
}

/**
 * Chat message wrapper that applies VIP styling
 */
export function VIPChatMessage({ children, isVIP = false }) {
  if (!isVIP) return children;

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 rounded animate-pulse" />
      <div className="relative border-l-2 border-amber-500 pl-2">
        {children}
      </div>
    </div>
  );
}
